import { Link } from 'react-router-dom';
import { MODULE_GROUP_LABELS, MODULES, type ModuleConfig, type ModuleGroup } from '../lib/modules.ts';
import { canSeeModule } from '../lib/role.tsx';
import type { Role } from '../lib/role.ts';
import { Icon, ICON_NAMES, type IconName } from './Icon.tsx';

interface SidebarProps {
  current: string;
  counts: Partial<Record<string, number>>;
  userInfo: {
    name: string;
    role: string;
    city?: string;
  };
}

const GROUP_ORDER: ModuleGroup[] = ['main', 'operations', 'management', 'support'];

function isIconName(iconKey: string): iconKey is IconName {
  return (ICON_NAMES as readonly string[]).includes(iconKey);
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'A';
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function normalizePath(value: string): string {
  return value.replace(/^\/+|\/+$/g, '');
}

function isActive(current: string, mod: ModuleConfig): boolean {
  const normalizedCurrent = normalizePath(current);
  const normalizedSlug = normalizePath(mod.slug);
  if (normalizedSlug === '') {
    return normalizedCurrent === '';
  }
  return normalizedCurrent === normalizedSlug || normalizedCurrent.startsWith(`${normalizedSlug}/`);
}

interface NavItemProps {
  mod: ModuleConfig;
  active: boolean;
  count?: number;
}

function NavItem({ mod, active, count }: NavItemProps) {
  const iconName = isIconName(mod.iconKey) ? mod.iconKey : 'dashboard';
  const content = (
    <>
      <Icon name={iconName} size={18} aria-hidden="true" />
      <span className="nav-label">{mod.label}</span>
      {typeof count === 'number' && count > 0 && <span className="count">{count}</span>}
      {mod.soon && <span className="soon-pill">Скоро</span>}
    </>
  );

  if (mod.soon) {
    return (
      <button className="nav-item" type="button" disabled aria-disabled="true" data-active={active ? 'true' : undefined}>
        {content}
      </button>
    );
  }

  const href = mod.slug === '' ? '/' : `/m/${mod.slug}`;
  return (
    <Link className="nav-item" to={href} aria-current={active ? 'page' : undefined} data-active={active ? 'true' : undefined}>
      {content}
    </Link>
  );
}

export function Sidebar({ current, counts, userInfo }: SidebarProps) {
  const role = userInfo.role as Role;
  const visibleModules = MODULES.filter((mod) => canSeeModule(role, mod));

  return (
    <nav className="sidebar" aria-label="Основная навигация">
      <div className="sidebar-logo">
        <img src="/logo-stamp.png" alt="" />
        <div>
          <div className="name">Mark & Post</div>
          <div className="role">Admin</div>
        </div>
      </div>

      <div className="sidebar-nav">
        {GROUP_ORDER.map((group) => {
          const modules = visibleModules.filter((mod) => mod.group === group);
          if (modules.length === 0) return null;
          const label = MODULE_GROUP_LABELS[group];
          return (
            <section className="nav-group" aria-label={label ?? 'Главное'} key={group} data-testid={`sidebar-group-${group}`}>
              {label && <div className="nav-group-title">{label}</div>}
              {modules.map((mod) => (
                <NavItem key={mod.id} mod={mod} active={isActive(current, mod)} count={counts[mod.id]} />
              ))}
            </section>
          );
        })}
      </div>

      <div className="sidebar-foot">
        <div className="avatar" aria-hidden="true">
          {initials(userInfo.name)}
        </div>
        <div>
          <div className="who">{userInfo.name}</div>
          <div className="sub">{userInfo.city ? `${userInfo.role} · ${userInfo.city}` : userInfo.role}</div>
        </div>
      </div>
    </nav>
  );
}
